import { ILoginState, IPostCompJoin, IPostLogin } from 'interface/login/loginInfo';
import { RootState } from 'store/rootReducer';
import { createSlice, createAsyncThunk, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import { makeData, makeHeader, makeJoinData } from 'libs/mindUtils';
import { PURGE } from 'redux-persist';
import isEmpty from 'lodash/isEmpty';

const initialState: ILoginState = {
  info: {
    code: '',
    message: '',
    accessToken: '',
    refreshToken: '',
    type: '',
    email: '',
  },
  loading: false,
  error: false,
};

export const postLogin = createAsyncThunk<AxiosResponse, IPostLogin, { state: RootState }>(
  'login/postLogin',
  ({ email, password }) => {
    const url = process.env.REACT_APP_URL;
    const header = makeHeader();
    const body = makeData(email, password);
    const instance = axios.create({
      validateStatus(status) {
        return status >= 200 && status <= 500;
      },
      timeout: 10000,
    });
    return instance.post(`${url}/api/admin/account/login`, body, header);
  },
);

export const postLoginCheck = createAsyncThunk<AxiosResponse, IPostLogin, { state: RootState }>(
  'login/postLoginCheck',
  ({ email, password }) => {
    const url = process.env.REACT_APP_URL;
    const header = makeHeader();
    const body = makeData(email, password);
    const instance = axios.create({
      validateStatus(status) {
        return status >= 200 && status <= 500;
      },
      timeout: 10000,
    });
    return instance.post(`${url}/api/admin/account/login`, body, header);
  },
);

export const postCompJoin = createAsyncThunk<AxiosResponse, IPostCompJoin, { state: RootState }>(
  'login/postCompJoin',
  ({ email, password, name, mobile, compName, compRole, compBizNum }) => {
    const url = process.env.REACT_APP_URL;
    const header = makeHeader();
    const body = makeJoinData({ email, password, name, mobile, compName, compRole, compBizNum });
    if (isEmpty(body.compBizNum)) {
      body.compBizNum = ''; // TODO 피그마에는 해당 값 입력받는 화면 없어서 하드코딩
    }
    const instance = axios.create({
      validateStatus(status) {
        return status >= 200 && status <= 500;
      },
      timeout: 10000,
    });
    return instance.post(`${url}/api/comp/join`, body, header);
  },
);

const loginSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    logout: () => initialState,
    reset: () => initialState,
  },
  extraReducers: (builder: ActionReducerMapBuilder<ILoginState>) => {
    builder
      .addCase(postLogin.fulfilled, (state, action) => {
        const { status } = action.payload;
        const { email } = action.meta.arg;
        if (status === 200) {
          const {
            data: { accessToken, refreshToken },
          } = action.payload;
          return {
            ...state,
            loading: false,
            error: false,
            info: {
              ...initialState.info,
              accessToken,
              refreshToken,
              email,
            },
          };
        }
        const {
          data: { code, message },
        } = action.payload;
        return {
          ...state,
          loading: false,
          error: true,
          info: {
            ...initialState.info,
            code,
            message: message || 'error',
          },
        };
      })
      .addCase(postLogin.pending, state => {
        state.loading = true;
        state.error = false;
      })
      .addCase(postLogin.rejected, (state, action) => {
        const { error } = action;
        return {
          ...state,
          loading: false,
          error: true,
          info: {
            ...initialState.info,
            message: error.message || 'error',
          },
        };
      })
      .addCase(PURGE, () => initialState);
  },
});
export const { logout, reset } = loginSlice.actions;
export default loginSlice.reducer;

export const loginSelector = (state: RootState): ILoginState => state.login;
